/* This file contains sagas related to patient's current visit */

import { put, call, select } from 'redux-saga/effects';
import * as CurrentVisitAPI from '../apis/currentVisit';
import {
    buildCurrentVisit,
    retrieveCurrentVisit,
    retrieveAllVisits,
    loginRetrieveSession,
    loadVisit,
    setVisitLoadingResult,
    loadAllVisits,
    buildAllVisits,
    setAllVisitsLoadingResult,
} from '../actions';
import { LoadingStatus, LoadingResult } from '../utils/LoadingUtils';
import { getUserId } from '../selectors';
import { isoMoment } from '../components/utilities/commonUtilities/dateTools';
import { VisitState } from '../utils/VisitTaskHandler';

export function* RetrieveCurrentVisitSaga() {
    yield put(loadVisit(LoadingStatus.STARTED));
    try {
        const response = yield call(CurrentVisitAPI.getCurrentVisit);
        if (response.data.hasVisit) {
            yield put(buildCurrentVisit(response.data.visit));
        } else {
            const future = yield call(CurrentVisitAPI.getFutureVisit);
            if (future.data.hasVisit) {
                yield put(buildCurrentVisit(future.data.visit));
            } else {
                // clear out the current visit if no visit.
                yield put(buildCurrentVisit(null));
            }
        }
        yield put(setVisitLoadingResult(LoadingResult.SUCCESSFUL));
    } catch (error) {
        if (!!error.response && error.response.status === 401) {
            // fill in with right error message
            console.log('Error accessing current visit');
        } else {
            // fill in with right error message
            console.log('all other error messages');
        }
        yield put(setVisitLoadingResult(LoadingResult.FAILED));
    }
    yield put(loadVisit(LoadingStatus.ENDED));
}

export function* CloseCurrentVisitSaga(action) {
    try {
        yield call(CurrentVisitAPI.closeCurrentVisit);
        if (action.refreshLoginSession) {
            yield put(loginRetrieveSession());
        }

        if (action.refreshVisit) {
            yield put(retrieveCurrentVisit());
            yield put(retrieveAllVisits());
        }
    } catch (error) {
        console.log(error);
    }
}

export function* RetrieveAllVisitsSaga() {
    yield put(loadAllVisits(LoadingStatus.STARTED));
    try {
        const userId = yield select(getUserId);
        const response = yield call(CurrentVisitAPI.getAllVisits, userId);
        const visits = response?.data?.entries ?? [];
        const filteredVisits = filterForValidVisits(visits);
        yield put(buildAllVisits(filteredVisits.sort(compareVisits)));
        yield put(setAllVisitsLoadingResult(LoadingResult.SUCCESSFUL));
    } catch (error) {
        if (!!error.response && error.response.status === 401) {
            // fill in with right error message
            console.log('Error accessing all visits');
        } else {
            // fill in with right error message
            console.log('all other error messages');
        }
        yield put(setAllVisitsLoadingResult(LoadingResult.FAILED));
    }
    yield put(loadAllVisits(LoadingStatus.ENDED));
}

// Compare function for sorting visits in ascending order according to taskOpenDate
function compareVisits(visitA, visitB) {
    const taskOpenDateA = isoMoment(visitA.taskOpenDate);
    const taskOpenDateB = isoMoment(visitB.taskOpenDate);

    if (taskOpenDateA.isBefore(taskOpenDateB)) {
        return -1;
    }
    if (taskOpenDateA.isAfter(taskOpenDateB)) {
        return 1;
    }
    // taskOpenDateA must be equal to taskOpenDateB
    return 0;
}

function filterForValidVisits(visits) {
    return visits
        ? visits.filter(visit => {
              return !!visit && visit.visitState !== VisitState.ABORTED && visit.visitState !== VisitState.CANCELLED;
          })
        : visits;
}
